/**
 * Approval status banner for the quote editor.
 * Shows contextual actions based on quote status and user role.
 *
 * Phase 2 Item 9: Declined and Withdrawn states now show a schedule release
 * banner. If `freshReleaseCount` is provided (set from the status-update API
 * response), we show "N machine bookings released." immediately.
 * When loading an already-Declined/Withdrawn quote, we fetch the release
 * summary from GET /api/schedule/quotes/:key/release-summary and show a
 * quieter "This quote's schedule has been released." banner.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { CheckCircleOutline, ErrorOutline, HourglassEmpty, PostAdd, Send } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../../hooks/usePermissions';
import { roleAtLeast, roleThresholdLabel } from '../../../constants/roles';
import { ROUTES } from '../../../constants/routes';
import {
  getApprovalTiers, getQuoteApproval, getQuoteEvents, submitForApproval,
  approveQuote, rejectQuote, convertQuoteToWorkOrder, markQuoteAccepted,
  getQuoteConvertedTarget, type ConvertedTarget,
} from '../../../services/quoteService';
import { useAuth } from '../../../auth/useAuth';
import { getQuoteReleaseSummary } from '../../../services/scheduleBookings';
import { MarkAcceptedDialog } from './MarkAcceptedDialog';
import { MarkSentDialog } from './MarkSentDialog';
import { MarkDeclinedDialog } from './MarkDeclinedDialog';
import { QuoteConvertedBanner } from './QuoteConvertedBanner';
import type { QuoteEvent, QuoteStatus } from '../../../types/quote';
import type { QuoteDetail } from '../../../types/quote';
import type { ApprovalTier, QuoteApproval } from '../../../types/approval';

interface Props {
  quote: QuoteDetail;
  onRefresh: () => void;
  /**
   * When provided, the status-update API just returned a scheduleRelease
   * payload with this booking count. We surface it as an immediate banner.
   * null = no fresh release (either other status or no bookings existed).
   */
  freshReleaseCount: number | null;
  /**
   * G2-3 (audit #2): status-transition callback (wired to the editor's
   * handleUpdateQuote so the committed-work 409 ceremony is handled there).
   * Powers the "Mark Expired" action on Sent/Viewed quotes.
   */
  onStatusChange?: (status: QuoteStatus) => void;
}

const formatCurrency = (value: number, currency: string) =>
  value.toLocaleString('en-CA', { style: 'currency', currency: currency || 'CAD' });

export const QuoteApprovalSection = ({ quote, onRefresh, freshReleaseCount, onStatusChange }: Props) => {
  const theme = useTheme();
  const { role: userRole } = usePermissions();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [tiers, setTiers] = useState<ApprovalTier[]>([]);
  const [approval, setApproval] = useState<QuoteApproval | null>(null);
  const [events, setEvents] = useState<QuoteEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [reason, setReason] = useState('');

  /**
   * Release summary fetched from the server when this component mounts on an
   * already-Declined/Withdrawn quote (i.e. freshReleaseCount is not available).
   */
  const [persistedRelease, setPersistedRelease] = useState<{
    tasksReleased: number;
    bookingsReleased: number;
  } | null>(null);

  // G2-3: 'Expired' releases the schedule exactly like Declined/Withdrawn.
  const isTerminalReleased =
    quote.Status === 'Declined' || quote.Status === 'Withdrawn' || quote.Status === 'Expired';

  /** G2-3: quote → work-order conversion state (Accepted branch). */
  const [converting, setConverting] = useState(false);
  const [convertConfirmOpen, setConvertConfirmOpen] = useState(false);
  const [convertError, setConvertError] = useState<string | null>(null);
  const [expireConfirmOpen, setExpireConfirmOpen] = useState(false);

  /** 2026-08-06: the WO + project this Converted quote became — the banner
   *  names them and links to the work order. */
  const [convertedTarget, setConvertedTarget] = useState<ConvertedTarget | null>(null);
  useEffect(() => {
    if (quote.Status !== 'Converted') { setConvertedTarget(null); return; }
    let cancelled = false;
    getQuoteConvertedTarget(quote.QuoteKey).then((t) => {
      if (!cancelled) setConvertedTarget(t);
    });
    return () => { cancelled = true; };
  }, [quote.Status, quote.QuoteKey]);

  /**
   * Quote lifecycle Phase 1: manual "Mark as Accepted" (verbal / email /
   * signed-offline — and the only acceptance path for Internal quotes).
   * Same role gate as conversion, which it unlocks.
   */
  const [markAcceptedOpen, setMarkAcceptedOpen] = useState(false);
  // RBAC Phase A/B (2026-08-06): quote lifecycle actions are admin-only.
  const canMarkAccepted = roleAtLeast(userRole, 'admin');
  const handleMarkedAccepted = () => {
    setMarkAcceptedOpen(false);
    onRefresh(); // parent refetch → Status='Accepted' → Accepted branch (Create Work Order)
    fetchData();
  };

  /**
   * Manual status overrides (2026-07-24): the quote was sent, or answered,
   * outside the system. Mark as Sent covers "emailed from Outlook"; Mark as
   * Declined covers a verbal/email "no". Same role gate as Mark as Accepted.
   */
  const [markSentOpen, setMarkSentOpen] = useState(false);
  const [markDeclinedOpen, setMarkDeclinedOpen] = useState(false);
  const handleMarkedSent = () => {
    setMarkSentOpen(false);
    onRefresh(); // parent refetch → Status='Sent' → waiting-for-client branch
    fetchData();
  };
  const handleMarkedDeclined = () => {
    setMarkDeclinedOpen(false);
    // Parent refetch flips Status to 'Declined'; the terminal branch then
    // fetches the release summary itself (persistedRelease path).
    onRefresh();
    fetchData();
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tiersData, approvalData] = await Promise.all([
        getApprovalTiers(),
        getQuoteApproval(quote.QuoteKey),
      ]);
      setTiers(tiersData);
      setApproval(approvalData);
      try { setEvents(await getQuoteEvents(quote.QuoteKey)); } catch { /* */ }

      // Fetch release summary for Declined/Withdrawn quotes when no fresh count
      // is available (i.e., the quote was already in this state on page load).
      if (isTerminalReleased && freshReleaseCount === null) {
        try {
          const summary = await getQuoteReleaseSummary(quote.QuoteKey);
          setPersistedRelease(summary);
        } catch { /* non-fatal — banner simply omitted */ }
      }
    } catch { /* */ }
    finally { setLoading(false); }
  }, [quote.QuoteKey, quote.Status, isTerminalReleased, freshReleaseCount]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const requiredTier = tiers.find((t) =>
    quote.GrandTotal >= t.MinAmount && (t.MaxAmount === null || quote.GrandTotal <= t.MaxAmount),
  );
  const requiredRole = approval?.RequiredRole || requiredTier?.RequiredRole || 'admin';
  // RBAC Phase B: admin-only regardless of legacy tier stamped on old rows.
  const canApprove = roleAtLeast(userRole, 'admin');

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await submitForApproval(quote.QuoteKey);
      onRefresh();
      fetchData();
    } catch { /* */ }
    finally { setSubmitting(false); }
  };

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await approveQuote(quote.QuoteKey, comment || undefined);
      setApproveOpen(false);
      setComment('');
      navigate(`${ROUTES.QUOTES}?highlight=${quote.QuoteKey}`);
    } catch { /* */ }
    finally { setSubmitting(false); }
  };

  const handleReject = async () => {
    if (!reason.trim()) return;
    setSubmitting(true);
    try {
      await rejectQuote(quote.QuoteKey, reason);
      setRejectOpen(false);
      setReason('');
      navigate(`${ROUTES.QUOTES}?highlight=${quote.QuoteKey}`);
      fetchData();
    } catch { /* */ }
    finally { setSubmitting(false); }
  };

  /**
   * G2-3 (audit #2): one-click quote → work order. The backend creates the WO
   * (+ project/placeholder sample) from the quote's test lines, commits the
   * tentative holds, migrates the schedule onto the WO (no double-count) and
   * flips the quote to 'Converted'. On success we navigate straight to the WO.
   */
  const handleConvert = async () => {
    setConverting(true);
    setConvertError(null);
    try {
      const result = await convertQuoteToWorkOrder(quote.QuoteKey);
      setConvertConfirmOpen(false);
      navigate(
        `/projects/${result.projectId}/workorders/${result.workOrderKey}`,
        {
          state: {
            convertedFromQuote: {
              quoteNumber: quote.QuoteNumber,
              detailsCreated: result.detailsCreated,
              tasksMigrated: result.tasksMigrated,
              unmappedLines: result.unmappedLines,
              placeholderTests: result.placeholderTests ?? [],
            },
          },
        },
      );
    } catch (err) {
      const resp = (err as {
        response?: { data?: { code?: string; error?: string } };
      }).response;
      setConvertError(resp?.data?.error
        ?? (err instanceof Error ? err.message : 'Failed to create the work order.'));
    } finally {
      setConverting(false);
    }
  };

  /**
   * 2026-08-04 (workflow clarity): internal quotes accept their own work —
   * one click records the acceptance AND creates the work order. The backend
   * allows Draft→Accepted for QuoteType 'Internal' only.
   */
  const handleAcceptAndConvert = async () => {
    setConverting(true);
    setConvertError(null);
    try {
      await markQuoteAccepted(quote.QuoteKey, {
        acceptedByName: user?.displayName || user?.email || 'Internal',
        acceptedByEmail: user?.email ?? undefined,
        channel: 'verbal',
        note: 'Internal work — accepted and converted in one step.',
      });
    } catch (err) {
      const resp = (err as { response?: { data?: { error?: string } } }).response;
      setConvertError(resp?.data?.error ?? (err instanceof Error ? err.message : 'Could not accept the quote.'));
      setConverting(false);
      return;
    }
    await handleConvert();
  };

  if (loading) return null;

  const baseSx = {
    p: 2, mb: 2, borderRadius: 2,
    border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
  };

  // ── Rejected — show reason and allow edit + resubmit ─────────────────────
  if (quote.Status === 'Rejected') {
    return (
      <Box sx={{ ...baseSx, backgroundColor: alpha(theme.palette.error.main, 0.06) }}>
        <Alert severity="error" icon={<ErrorOutline />} sx={{ mb: 1.5 }}>
          Rejected by <strong>{approval?.ApproverName || 'Unknown'}</strong>
          {approval?.DecidedAt && ` on ${new Date(approval.DecidedAt).toLocaleDateString('en-CA')}`}
          : {approval?.Comment || 'No reason given'}
        </Alert>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            Edit the quote and resubmit when ready.
          </Typography>
          <Box sx={{ flex: 1 }} />
          {canMarkAccepted && (
            <Button variant="contained" size="small" startIcon={<Send />} disabled={submitting}
              onClick={handleSubmit} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>
              {submitting ? 'Submitting...' : 'Resubmit for Approval'}
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  // ── Changes Requested — client asked for changes ──────────────────────────
  if (quote.Status === 'Changes Requested') {
    const changeRequest = events.find((e) => e.EventType === 'ChangesRequested');
    return (
      <Box sx={{ ...baseSx, backgroundColor: alpha(theme.palette.warning.main, 0.08) }}>
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>Client requested changes:</Typography>
          <Typography variant="body2">{changeRequest?.Details || 'No details provided'}</Typography>
          <Typography variant="caption" color="text.secondary">
            — {changeRequest?.UserId}
            {changeRequest?.Timestamp && ` on ${new Date(changeRequest.Timestamp).toLocaleDateString('en-CA')}`}
          </Typography>
        </Alert>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            Make the requested changes and resubmit when ready.
          </Typography>
          <Box sx={{ flex: 1 }} />
          {canMarkAccepted && (
            <Button variant="contained" size="small" startIcon={<Send />} disabled={submitting}
              onClick={handleSubmit} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>
              {submitting ? 'Submitting...' : 'Resubmit for Approval'}
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  // ── Draft — show submit option (internal quotes get the one-click door) ──
  if (quote.Status === 'Draft') {
    const isInternal = quote.QuoteType === 'Internal';
    return (
      <Box sx={{ ...baseSx, backgroundColor: alpha(theme.palette.info.main, 0.06) }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <HourglassEmpty sx={{ color: 'text.secondary', fontSize: 20 }} />
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 260 }}>
            {isInternal ? (
              <>
                <strong>Internal quote</strong> ({formatCurrency(quote.GrandTotal, quote.Currency)}) —
                no client approval needed. Accept it and the work order is created in one step,
                or submit for approval if you still want a sign-off.
              </>
            ) : (
              <>
                This quote ({formatCurrency(quote.GrandTotal, quote.Currency)}) requires approval
                from: <strong>{roleThresholdLabel(requiredRole)}</strong>
              </>
            )}
          </Typography>
          {isInternal && canMarkAccepted && (
            <Button variant="contained" size="small" disabled={converting}
              startIcon={converting ? <CircularProgress size={14} color="inherit" /> : <PostAdd />}
              onClick={handleAcceptAndConvert}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', flexShrink: 0 }}>
              Accept &amp; Create Work Order
            </Button>
          )}
          {canMarkAccepted && (
            <Button variant={isInternal ? 'outlined' : 'contained'} size="small" startIcon={<Send />} disabled={submitting}
              onClick={handleSubmit} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', flexShrink: 0 }}>
              Submit for Approval
            </Button>
          )}
        </Box>
        {convertError && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setConvertError(null)}>{convertError}</Alert>
        )}
      </Box>
    );
  }

  // ── Internal Review — awaiting approval ───────────────────────────────────
  if (quote.Status === 'Internal Review') {
    return (
      <>
        <Box sx={{ ...baseSx, backgroundColor: alpha(theme.palette.warning.main, 0.06) }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <HourglassEmpty sx={{ color: 'warning.main', fontSize: 20 }} />
            <Typography variant="body2">
              Awaiting approval from: <strong>{roleThresholdLabel(requiredRole)}</strong>
            </Typography>
            <Box sx={{ flex: 1 }} />
            {canApprove ? (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" color="success" size="small"
                  onClick={() => setApproveOpen(true)} disabled={submitting}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>
                  Approve
                </Button>
                <Button variant="outlined" color="error" size="small"
                  onClick={() => setRejectOpen(true)} disabled={submitting}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>
                  Reject
                </Button>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                You don't have permission to approve this quote
              </Typography>
            )}
          </Box>
        </Box>

        {/* Approve dialog */}
        <Dialog open={approveOpen} onClose={() => setApproveOpen(false)} maxWidth="xs" fullWidth
          PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
          <DialogTitle sx={{ fontWeight: 700 }}>Approve Quote</DialogTitle>
          <DialogContent>
            <TextField label="Comment (optional)" fullWidth multiline minRows={2} size="small"
              value={comment} onChange={(e) => setComment(e.target.value)} sx={{ mt: 1 }} />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setApproveOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button variant="contained" color="success" onClick={handleApprove} disabled={submitting}
              sx={{ textTransform: 'none', fontWeight: 600 }}>
              {submitting ? <CircularProgress size={18} /> : 'Approve'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reject dialog */}
        <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="xs" fullWidth
          PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
          <DialogTitle sx={{ fontWeight: 700 }}>Reject Quote</DialogTitle>
          <DialogContent>
            <TextField label="Reason for rejection" fullWidth multiline minRows={2} size="small" required
              value={reason} onChange={(e) => setReason(e.target.value)} sx={{ mt: 1 }} />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setRejectOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button variant="contained" color="error" onClick={handleReject}
              disabled={submitting || !reason.trim()}
              sx={{ textTransform: 'none', fontWeight: 600 }}>
              {submitting ? <CircularProgress size={18} /> : 'Reject'}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  // ── Approved Internal ─────────────────────────────────────────────────────
  if (quote.Status === 'Approved Internal') {
    return (
      <Box sx={{ ...baseSx, backgroundColor: alpha(theme.palette.success.main, 0.06) }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CheckCircleOutline sx={{ color: 'success.main', fontSize: 20 }} />
          <Typography variant="body2" sx={{ flex: 1 }}>
            Approved by <strong>{approval?.ApproverName || 'Unknown'}</strong>
            {approval?.DecidedAt && ` on ${new Date(approval.DecidedAt).toLocaleDateString('en-CA')}`}
          </Typography>
          {/* Manual overrides (2026-07-24): the quote went out / was answered
              outside the system — record what actually happened. */}
          {canMarkAccepted && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => setMarkSentOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', flexShrink: 0 }}
            >
              Mark as Sent…
            </Button>
          )}
          {/* Phase 1: record a verbal/email acceptance without sending — the
              only acceptance door for Internal quotes. */}
          {canMarkAccepted && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => setMarkAcceptedOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', flexShrink: 0 }}
            >
              Mark as Accepted…
            </Button>
          )}
          {canMarkAccepted && (
            <Button
              size="small"
              color="error"
              variant="outlined"
              onClick={() => setMarkDeclinedOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', flexShrink: 0 }}
            >
              Mark as Declined…
            </Button>
          )}
        </Box>
        {approval?.Comment && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, ml: 4.5 }}>
            {approval.Comment}
          </Typography>
        )}
        <MarkAcceptedDialog open={markAcceptedOpen} quoteKey={quote.QuoteKey}
          onClose={() => setMarkAcceptedOpen(false)} onAccepted={handleMarkedAccepted} />
        <MarkSentDialog open={markSentOpen} quoteKey={quote.QuoteKey}
          onClose={() => setMarkSentOpen(false)} onSent={handleMarkedSent} />
        <MarkDeclinedDialog open={markDeclinedOpen} quoteKey={quote.QuoteKey}
          onClose={() => setMarkDeclinedOpen(false)} onDeclined={handleMarkedDeclined} />
      </Box>
    );
  }

  // ── Sent / Viewed — waiting for client ───────────────────────────────────
  if (quote.Status === 'Sent' || quote.Status === 'Viewed') {
    const sentEvent = events.find((e) => e.EventType === 'Sent');
    const pastValidUntil =
      !!quote.ValidUntil &&
      quote.ValidUntil.slice(0, 10) < new Date().toLocaleDateString('en-CA');
    return (
      <Box sx={{ ...baseSx, backgroundColor: alpha(theme.palette.info.main, 0.06) }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Send sx={{ color: 'info.main', fontSize: 20 }} />
          <Typography variant="body2" sx={{ flex: 1 }}>
            {quote.Status === 'Viewed' ? 'Client has viewed this quote' : 'Quote sent to client'}
            {sentEvent?.Details && ` — ${sentEvent.Details}`}
          </Typography>
          {/* Phase 1: the client answered out-of-band (verbal/email) — record
              the acceptance so conversion unlocks. */}
          {canMarkAccepted && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => setMarkAcceptedOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', flexShrink: 0 }}
            >
              Mark as Accepted…
            </Button>
          )}
          {/* Manual override (2026-07-24): the client said no verbally or by
              email — record the decline with the reason. */}
          {canMarkAccepted && (
            <Button
              size="small"
              color="error"
              variant="outlined"
              onClick={() => setMarkDeclinedOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', flexShrink: 0 }}
            >
              Mark as Declined…
            </Button>
          )}
          {/* G2-3: 'Expired' is a real status now — expiring releases the
              quote's tentative holds like Declined (committed work keeps the
              409 confirm ceremony, handled by the editor's onStatusChange). */}
          {onStatusChange && (
            <Button
              size="small"
              color="warning"
              variant="outlined"
              onClick={() => setExpireConfirmOpen(true)}
              sx={{ textTransform: 'none', flexShrink: 0 }}
            >
              Mark Expired
            </Button>
          )}
        </Box>
        {pastValidUntil && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            <Typography variant="body2">
              This quote's validity ended {new Date(quote.ValidUntil as string).toLocaleDateString('en-CA')}.
              Mark it Expired to release its tentative schedule holds.
            </Typography>
          </Alert>
        )}
        <Dialog open={expireConfirmOpen} onClose={() => setExpireConfirmOpen(false)}>
          <DialogTitle>Mark quote Expired?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              The quote's tentative schedule holds will be released — machines and
              people become available again. If committed work is attached you
              will be asked to confirm the release separately.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExpireConfirmOpen(false)}>Cancel</Button>
            <Button
              color="warning"
              variant="contained"
              onClick={() => { setExpireConfirmOpen(false); onStatusChange?.('Expired'); }}
            >
              Mark Expired
            </Button>
          </DialogActions>
        </Dialog>
        <MarkAcceptedDialog open={markAcceptedOpen} quoteKey={quote.QuoteKey}
          onClose={() => setMarkAcceptedOpen(false)} onAccepted={handleMarkedAccepted} />
        <MarkDeclinedDialog open={markDeclinedOpen} quoteKey={quote.QuoteKey}
          onClose={() => setMarkDeclinedOpen(false)} onDeclined={handleMarkedDeclined} />
      </Box>
    );
  }

  // ── Client Accepted ───────────────────────────────────────────────────────
  if (quote.Status === 'Accepted') {
    const acceptEvent = events.find((e) => e.EventType === 'Accepted');
    const canConvert = roleAtLeast(userRole, 'admin');
    return (
      <Box sx={{ ...baseSx, backgroundColor: alpha(theme.palette.success.main, 0.08) }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CheckCircleOutline sx={{ color: 'success.main', fontSize: 20 }} />
          <Typography variant="body2" sx={{ flex: 1 }}>
            Client <strong>accepted</strong> this quote
            {quote.AcceptedByName && ` — signed by ${quote.AcceptedByName}`}
            {quote.AcceptedAt && ` on ${new Date(quote.AcceptedAt).toLocaleDateString('en-CA')}`}
          </Typography>
          {/* G2-3 (audit #2): the missing happy-path door — one click creates
              the WO from the quote's lines and carries the schedule over. */}
          {canConvert && (
            <Button
              size="small"
              variant="contained"
              startIcon={converting ? <CircularProgress size={14} color="inherit" /> : <PostAdd />}
              disabled={converting}
              onClick={() => { setConvertError(null); setConvertConfirmOpen(true); }}
              sx={{ textTransform: 'none', flexShrink: 0 }}
            >
              Create Work Order
            </Button>
          )}
        </Box>
        {acceptEvent?.Details && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, ml: 4.5 }}>
            {acceptEvent.Details}
          </Typography>
        )}
        <Dialog open={convertConfirmOpen} onClose={() => !converting && setConvertConfirmOpen(false)}>
          <DialogTitle>Create Work Order from this quote?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Creates a new project and work order with one test row per quoted
              test line (a placeholder sample is used until the physical samples
              arrive). The quote's schedule follows the work order: tentative
              bookings and staffing are committed and re-linked — nothing is
              double-booked. The quote becomes <strong>Converted</strong>.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Test lines without a lab-test mapping are reported and left on the
              quote — they are never silently dropped.
            </Typography>
            {convertError && <Alert severity="error" sx={{ mt: 1.5 }}>{convertError}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConvertConfirmOpen(false)} disabled={converting}>Cancel</Button>
            <Button variant="contained" onClick={handleConvert} disabled={converting}>
              {converting ? 'Creating…' : 'Create Work Order'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // ── Converted — the quote's work moved to a work order (G2-3) ────────────
  // QuoteConvertedBanner names the WO + project and links there; when that WO
  // has since been deleted (explicit link, migration 129) it says so instead.
  if (quote.Status === 'Converted') {
    return (
      <QuoteConvertedBanner
        baseSx={baseSx}
        target={convertedTarget}
        convertEvent={events.find((e) => e.EventType === 'Converted')}
      />
    );
  }

  // ── Client Declined, Withdrawn, or Expired ───────────────────────────────
  // Phase 2 Item 9 (G2-3 adds Expired): show schedule release banner.
  if (isTerminalReleased) {
    const declineEvent = events.find((e) => e.EventType === 'Declined');
    const isWithdrawn = quote.Status === 'Withdrawn';
    const isExpired = quote.Status === 'Expired';

    // Determine what to show for the release banner:
    //   - freshReleaseCount: set from the API response right after transition
    //   - persistedRelease: fetched from the server on page load (already-declined quote)
    const releaseBookings = freshReleaseCount !== null
      ? freshReleaseCount
      : (persistedRelease?.bookingsReleased ?? null);

    const releaseBannerText = (() => {
      if (releaseBookings === null) return null;
      if (releaseBookings > 0) {
        return `${releaseBookings} machine booking${releaseBookings === 1 ? '' : 's'} released — time is now available for other projects.`;
      }
      return "This quote's schedule has been released.";
    })();

    return (
      <Box sx={{ ...baseSx, backgroundColor: alpha(theme.palette.error.main, 0.06) }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ErrorOutline sx={{ color: 'error.main', fontSize: 20 }} />
          <Typography variant="body2">
            {isWithdrawn
              ? 'This quote has been withdrawn'
              : isExpired
                ? 'This quote has expired'
                : 'Client declined this quote'}
            {quote.AcceptedAt && ` on ${new Date(quote.AcceptedAt).toLocaleDateString('en-CA')}`}
          </Typography>
        </Box>
        {declineEvent?.Details && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>Reason from client:</Typography>
            <Typography variant="body2">{declineEvent.Details}</Typography>
          </Alert>
        )}
        {releaseBannerText && (
          <Alert
            severity="info"
            sx={{
              mt: 1,
              backgroundColor: alpha(theme.palette.info.main, 0.08),
              '& .MuiAlert-icon': { color: 'info.main' },
            }}
          >
            <Typography variant="body2">{releaseBannerText}</Typography>
          </Alert>
        )}
      </Box>
    );
  }

  return null;
};
